import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { Branch } from 'src/app/core/interfaces/invoice.interface';
import { BranchService } from 'src/app/core/services/branch.service';
import { DropdownInputsSignalService } from 'src/app/core/services/dropdown-inputs-signal.service';
import { CompanyService } from 'src/app/modules/settings/services/company.service';
import { LogoStateService, DEFAULT_LOGO_URL } from 'src/app/core/services/logo-state.service';

@Component({
  selector: 'app-selected-branch-header',
  templateUrl: './selected-branch-header.component.html',
  styleUrls: ['./selected-branch-header.component.scss']
})
export class SelectedBranchHeaderComponent implements OnInit, OnDestroy {
  branch?: Branch;
  private destroy$ = new Subject<void>();

  /** Fallback placeholder — absolute path so it works in print views. */
  readonly logoSrc = '/assets/media/logos/company-placeholder.png';

  /** Resolved logo URL from centralized state (branch → company → default). */
  displayLogoUrl: string = DEFAULT_LOGO_URL;

  companyNameAr?: string;
  companyNameEn?: string;

  constructor(
    private branchService: BranchService,
    private signalService: DropdownInputsSignalService,
    private companyService: CompanyService,
    private router: Router,
    private logoState: LogoStateService,
  ) { }

  ngOnInit(): void {
    // ── Subscribe to centralized logo state ──────────────────────
    this.logoState.displayLogo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => { this.displayLogoUrl = url; });

    // ── Load company name (display-only, not in shared logo state) ─
    this.loadCompanyName();

    // ── React to company name changes (logo already handled by service) ──
    this.signalService.reload$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res === 'updateCompanyLogo') {
          // Refresh company name too in case it changed
          this.loadCompanyName();
        }
      });

    // ── Branch data (name, phone, address — not logo) ────────────
    this.loadSelectedBranch();
    this.setupBranchChangeListener();
    this.setupNavigationListener();
  }

  // ─────────────────────────────────────────────────────────────
  // Company name (display only — logo is in LogoStateService)
  // ─────────────────────────────────────────────────────────────

  private loadCompanyName(): void {
    this.companyService.getDefault(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const company = res?.data;
          this.companyNameAr = company?.nameAr ?? '';
          this.companyNameEn = company?.nameEn ?? '';
        },
        error: (err) => { console.log(err); }
      });
  }

  // ─────────────────────────────────────────────────────────────
  // Branch data (structural info — name, phone, address)
  // ─────────────────────────────────────────────────────────────

  private loadSelectedBranch(): void {
    const selectedBranchId = this.getSelectedBranchId();

    if (selectedBranchId) {
      const cachedBranch = this.getSelectedBranchObject();
      // if (cachedBranch && Number(cachedBranch.id) === Number(selectedBranchId)) {
      //   this.branch = cachedBranch;

      //   return;
      // }
        this.fetchBranchFromApi(selectedBranchId);
    }

  
  }

  private fetchBranchFromApi(selectedBranchId: number | null): void {
    const branchSource$ = selectedBranchId
      ? this.branchService.getBranch(selectedBranchId)
      : this.branchService.getDefaultBranch(true);

    branchSource$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const raw = (res as any)?.data ?? res;
          console.log(raw);
          this.branch = raw && typeof raw === 'object' ? raw as Branch : undefined;
          console.log('branch data : ',this.branch);
          if (this.branch && (this.branch.id || selectedBranchId)) {
            this.saveSelectedBranchObject(this.branch);
          }
        },
        error: (err) => { console.error('Error loading branch:', err); }
      });
  }

  private setupBranchChangeListener(): void {
    this.signalService.reload$
      .pipe(
        filter(signal => signal === 'branchSelected'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Force fresh fetch from API (bypass cache) so name/address are up to date
        // Logo is updated immediately via LogoStateService (already triggered by signal)
        this.fetchBranchFromApi(this.getSelectedBranchId());
      });
  }

  private setupNavigationListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => { this.loadSelectedBranch(); });
  }

  // ─────────────────────────────────────────────────────────────
  // Storage helpers
  // ─────────────────────────────────────────────────────────────

  private getSelectedBranchId(): number | null {
    const userProfileString = sessionStorage.getItem('userProfile');
    const userId = userProfileString ? JSON.parse(userProfileString)?.id : null;
    const selectedBranchKey = userId ? `selected_branch_id_user_${userId}` : 'selected_branch_id';
    const storedId = localStorage.getItem(selectedBranchKey);
    if (!storedId) return null;
    const parsedId = Number(storedId);
    return isNaN(parsedId) ? null : parsedId;
  }

  private getSelectedBranchObject(): Branch | null {
    const userProfileString = sessionStorage.getItem('userProfile');
    const userId = userProfileString ? JSON.parse(userProfileString)?.id : null;
    const key = userId ? `selected_branch_object_user_${userId}` : 'selected_branch_object';
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    try { return JSON.parse(stored) as Branch; } catch { return null; }
  }

  private saveSelectedBranchObject(branch: Branch): void {
    const userProfileString = sessionStorage.getItem('userProfile');
    const userId = userProfileString ? JSON.parse(userProfileString)?.id : null;
    const key = userId ? `selected_branch_object_user_${userId}` : 'selected_branch_object';
    localStorage.setItem(key, JSON.stringify(branch));
  }

  // ─────────────────────────────────────────────────────────────
  // Template helpers
  // ─────────────────────────────────────────────────────────────

  /** Fallback when the browser fails to load the logo image. */
  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = this.logoSrc;
      img.onerror = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formattedAddress(): string {
    if (!this.branch) return '';
    return [
      this.branch.cityName,
      this.branch.citySubdivisionName,
      this.branch.streetName
    ].filter(Boolean).join(' – ');
  }

  getFormattedBuilding(isEn: boolean): string {
    if (!this.branch) return '';
    const parts = [];
    if (this.branch.additionalStreetName) {
      parts.push(this.branch.additionalStreetName);
    }
    if (this.branch.buildingNumber) {
      parts.push(isEn ? `Office ${this.branch.buildingNumber}` : `مكتب ${this.branch.buildingNumber}`);
    }
    return parts.join(' – ');
  }
}

